import { UserScope } from '@alliance/ats/graphql-client'
import { AccessApiService } from '@alliance/ats/access/api'
import { AuthApiService } from '@alliance/ats/auth/api'
import { CurrentUserApiService } from '@alliance/ats/users/api'
import { GetHeaderNavigationButtonEvent, HeaderNavigationButtonEvent, ProjectCreateButtonEvent, PushAnalyticsEventsService } from '@alliance/ats/analytics/utils'
import { RxStateComponent } from '@alliance/shared/models'
import {
  IconAnalytics,
  IconAnalytics20,
  IconCalendar,
  IconCalendarPublication20,
  IconCandidateCvBaseSearch20,
  IconCandidateSearch,
  IconLogin20,
  IconVacancy20,
  IconVacancyItemBold
} from '@alliance/shared/icons'
import { TranslationService } from '@alliance/shared/translation'
import { HEADER_BOTTOM_CONTENT, HeaderPositionModeEnum, HeaderSubHeaderModeEnum } from '@alliance/shared/header/utils'
import { ChangeDetectionStrategy, Component, Inject, Input } from '@angular/core'
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal'
import { combineLatest, map, Observable } from 'rxjs'
import { Translations } from '../localization/translations'
import { HeaderButton, HeaderItem, HeaderLinkWithEvent } from '../models/header-link-item'
import { AtsStaticTooltipParentRefEnum, MediaService } from '@alliance/shared/utils'
import { StaticTooltipViewHelperService } from '@alliance/ats/ui/static-tooltip'

@Component({
  selector: 'alliance-ats-header',
  templateUrl: './ats-header.component.html',
  styleUrls: ['./ats-header.component.tw.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AtsHeaderComponent extends RxStateComponent<{
  isLoggedIn: boolean
  hasAccess: boolean
  isSmallScreen: boolean
  showManager: boolean
  navigationLinks: HeaderLinkWithEvent[]
  navigationButtons: HeaderButton[]
  bottomContent: ComponentPortal<unknown> | TemplatePortal<unknown> | null
  positionMode: HeaderPositionModeEnum
  subHeaderMode: HeaderSubHeaderModeEnum
  tooltipText: string
}> {
  public readonly HEADER_NAVIGATION_EVENT = HeaderNavigationButtonEvent
  public readonly ICONS = {
    IconLogin20
  }
  public readonly state = this.connectViewState([
    'hasAccess',
    'isLoggedIn',
    'isSmallScreen',
    'bottomContent',
    'navigationLinks',
    'navigationButtons',
    'positionMode',
    'showManager',
    'subHeaderMode',
    'tooltipText'
  ])
  public readonly staticTooltipImage = 'https://images.cf-rabota.com.ua/alliance-ats/static-tooltips/create-project.svg'
  public readonly AtsStaticTooltipParentRefEnum = AtsStaticTooltipParentRefEnum
  public readonly HeaderSubHeaderModeEnum = HeaderSubHeaderModeEnum

  private readonly navigationLinks: HeaderLinkWithEvent[] = [
    {
      path: '/projects',
      label: this.translationService.translate(this.translations.projects),
      isLabelOptional: false,
      defaultIcon: IconVacancy20.name,
      activeIcon: IconVacancyItemBold.name,
      event: GetHeaderNavigationButtonEvent('projects'),
      dependsOnRight: null
    },
    {
      path: '/candidates',
      label: this.translationService.translate(this.translations.candidatesBase),
      isLabelOptional: false,
      defaultIcon: IconCandidateCvBaseSearch20.name,
      activeIcon: IconCandidateSearch.name,
      event: GetHeaderNavigationButtonEvent('candidates'),
      dependsOnRight: UserScope.ProjectCreation
    },
    {
      path: '/interviews',
      label: this.translationService.translate(this.translations.interviews),
      isLabelOptional: true,
      defaultIcon: IconCalendarPublication20.name,
      activeIcon: IconCalendar.name,
      event: GetHeaderNavigationButtonEvent('interviews'),
      dependsOnRight: UserScope.ProjectCreation
    },
    {
      path: '/analytics',
      label: this.translationService.translate(this.translations.analytics),
      isLabelOptional: true,
      defaultIcon: IconAnalytics20.name,
      activeIcon: IconAnalytics.name,
      event: GetHeaderNavigationButtonEvent('projects-analytics'),
      dependsOnRight: UserScope.ProjectCreation,
      tooltipParents: [AtsStaticTooltipParentRefEnum.landingRedirectAnalytics]
    }
  ]

  private readonly navigationButtons: HeaderButton[] = [
    {
      path: '/projects/create',
      label: this.translationService.translate(this.translations.createProject),
      isLabelOptional: false,
      dependsOnRight: UserScope.ProjectCreation,
      action: (): void => {
        this.pushEventsService.pushEvent({ event: HeaderNavigationButtonEvent, labelParam: 'create_project' })
        this.pushEventsService.pushEvent({ event: ProjectCreateButtonEvent, labelParam: 'header' })
      },
      tooltipParents: [AtsStaticTooltipParentRefEnum.createProject]
    }
  ]

  public constructor(
    @Inject(HEADER_BOTTOM_CONTENT)
    private headerBottomContent$: Observable<ComponentPortal<unknown> | TemplatePortal<unknown> | null>,
    public translations: Translations,
    private accessApiService: AccessApiService,
    private authApiService: AuthApiService,
    private currentUserApiService: CurrentUserApiService,
    private pushEventsService: PushAnalyticsEventsService,
    private translationService: TranslationService,
    private mediaService: MediaService,
    private staticTooltipViewHelperService: StaticTooltipViewHelperService
  ) {
    super()

    this.initState({
      isLoggedIn: this.authApiService.isLoggedIn$(),
      hasAccess: this.accessApiService.isAccessGranted$(),
      showManager: this.currentUserApiService.getUserHasRightTo$(UserScope.ProjectCreation),
      bottomContent: this.headerBottomContent$,
      navigationLinks: combineLatest([this.filterHeaderItemsByRights$(this.navigationLinks), this.select('isSmallScreen')]).pipe(
        map(([links, isSmall]) => links.map(item => ({ ...item, label: isSmall && item.isLabelOptional ? null : item.label })))
      ),
      navigationButtons: this.filterHeaderItemsByRights$(this.navigationButtons),
      positionMode: HeaderPositionModeEnum.sticky,
      isSmallScreen: this.mediaService.getScreen$('1200px'),
      subHeaderMode: HeaderSubHeaderModeEnum.default,
      tooltipText: this.staticTooltipViewHelperService.select('activeTooltipParentRef').pipe(
        map(parent => {
          const text = parent ? this.translations.tooltip[parent] : undefined
          return text ? this.translationService.translate(text) : ''
        })
      )
    })
  }

  @Input() public set positionMode(positionMode: HeaderPositionModeEnum) {
    this.set({ positionMode })
  }

  public trackByFn(index: number, item: HeaderItem): string | number {
    return item.path ?? index
  }

  private filterHeaderItemsByRights$<T extends HeaderItem>(items: T[]): Observable<T[]> {
    return this.currentUserApiService
      .getUserRights$()
      .pipe(map(userRights => items.reduce<T[]>((acc, link) => (!link.dependsOnRight || (link.dependsOnRight && userRights.includes(link.dependsOnRight)) ? [...acc, link] : acc), [])))
  }
}
